// cat. into common modules
import {createLogger} from '@into/into-common-logger';

import {DOMAIN_SCHEMA} from '../schema';
import {DOMAIN_ENUM} from '../enumMap';
import type {DomainName, DomainOf, DomainFields} from '../../../types/domain';

const log = createLogger('[Domain]');

type FieldMeta = {type: string; value?: unknown; maxLength?: number; enum?: string};

/** 서버 응답 원형 — 직렬화 계층의 입력. any 는 이 선언에서만 허용. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ServerRecord = Record<string, any>;
export type ServerData = ServerRecord | null | undefined;

/** `{...data, ...직렬화결과}` 를 반환하는 타입 */
export type SerializedWithRaw<N extends DomainName> = Partial<DomainFields<N>> & DomainOf<N>;

/**
 * 서버 응답 → 도메인 객체. flattenKeys 면 키가 `<도메인>.<필드>` 형태
 * - omit 을 주면 그 필드를 뺀 나머지를 방출 (역할별 축소 DTO)
 */
export function toDomain(
    domainName: string,
    serverData: ServerData,
    options: {flattenKeys?: boolean; omit?: readonly string[]} = {},
) {
    const {flattenKeys = true, omit} = options;

    if (!serverData) {
        return {};
    }

    const domain = (DOMAIN_SCHEMA as Record<string, Record<string, FieldMeta>>)[domainName];
    if (!domain) {
        log.warn(`Domain "${domainName}" not found`);
        return {};
    }

    if (omit) {
        const unknownKeys = omit.filter((f) => !(f in domain));
        if (unknownKeys.length) {
            log.warn(`Domain "${domainName}" has no field(s) to omit: ${unknownKeys.join(', ')}`);
        }
    }
    const omitted = omit ? new Set(omit) : null;
    const domainKeys = omitted ? Object.keys(domain).filter((k) => !omitted.has(k)) : Object.keys(domain);
    const convertedData: Record<string, unknown> = {};

    if (serverData.id !== undefined && serverData.id !== null) {
        convertedData.id = serverData.id;
    }

    domainKeys.forEach((domainKey) => {
        const fieldDef = domain[domainKey];
        const defaultValue = fieldDef?.value;

        let serverValue: unknown;
        if (domainKey.includes('.')) {
            const keys = domainKey.split('.');
            serverValue = keys.reduce<ServerRecord | undefined>((obj, key) => obj?.[key], serverData);
        } else {
            serverValue = serverData[domainKey];
        }

        const finalKey = flattenKeys ? `${domainName}.${domainKey}` : domainKey;
        convertedData[finalKey] = serverValue !== undefined && serverValue !== null
            ? serverValue
            : defaultValue;
    });

    return convertedData;
}

export function createDefault(domainName: string, options: {flattenKeys?: boolean} = {}) {
    const {flattenKeys = true} = options;

    const domain = (DOMAIN_SCHEMA as Record<string, Record<string, FieldMeta>>)[domainName];
    if (!domain) {
        log.warn(`Domain "${domainName}" not found`);
        return {};
    }

    const domainKeys = Object.keys(domain);
    const defaultData: Record<string, unknown> = {};

    domainKeys.forEach((domainKey) => {
        const fieldDef = domain[domainKey];
        const finalKey = flattenKeys ? `${domainName}.${domainKey}` : domainKey;
        defaultData[finalKey] = fieldDef?.value;
    });

    return defaultData;
}

export function getFieldMeta(domainName: string, fieldName: string): FieldMeta | null {
    const domain = (DOMAIN_SCHEMA as Record<string, Record<string, FieldMeta>>)[domainName];
    if (!domain) {
        return null;
    }
    // 스키마 필드 키는 "statusDate.deletedDate" 처럼 점을 포함한 평탄 문자열
    return domain[fieldName] || null;
}

export function getFieldMaxLength(domainName: string, fieldName: string): number | null {
    return getFieldMeta(domainName, fieldName)?.maxLength || null;
}

export function getEnumKey(enumName: string) {
    return (DOMAIN_ENUM as Record<string, string>)[enumName] || enumName || null;
}

/** 도메인 단건 직렬화. data 가 non-null 로 좁혀져 있으면 반환도 non-null. */
export function _serializeSingle<N extends DomainName>(domainName: N, data: ServerRecord): DomainOf<N>;
export function _serializeSingle<N extends DomainName>(domainName: N, data: ServerData): DomainOf<N> | null;
export function _serializeSingle<N extends DomainName>(domainName: N, data: ServerData): DomainOf<N> | null {
    if (!data) return null;
    return toDomain(domainName, data) as DomainOf<N>;
}

/**
 * 역할별 축소 DTO 전용 단건 직렬화 — 그 역할이 받지 않는 필드 제외
 */
export function _serializeExcept<N extends DomainName>(
    domainName: N,
    data: ServerData,
    omit: readonly string[],
): DomainOf<N> | null {
    if (!data) return null;
    return toDomain(domainName, data, {omit}) as DomainOf<N>;
}

/** _serializeExcept 리스트 */
export function _serializeExceptList<N extends DomainName>(
    domainName: N,
    data: unknown,
    omit: readonly string[],
): DomainOf<N>[] {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => toDomain(domainName, item, {omit}) as DomainOf<N>);
}

export function _serializeList<N extends DomainName>(domainName: N, data: unknown): DomainOf<N>[] {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => toDomain(domainName, item) as DomainOf<N>);
}

export function serializeHistoryChangeDetail(data: ServerData): SerializedWithRaw<"historyChangeDetail"> | null {
    if (!data) return null;
    return {
        ...data,
        ..._serializeSingle("historyChangeDetail", data),
    };
}

export function serializeHistoryChangeDetailList(data: unknown) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => serializeHistoryChangeDetail(item));
}
